import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const DetailDashboard = ({ currentUser, onRefresh }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [completingId, setCompletingId] = useState(null);
  const [extraChargeModal, setExtraChargeModal] = useState({ show: false, vehicle: null });
  const [extraCharge, setExtraCharge] = useState({ amount: 0, reason: '' });

  const loadVehicles = async () => {
    setLoading(true);
    try {
      // Get all vehicles that need detail or are in detail
      const allVehicles = await api.getVehicles();
      const detailVehicles = allVehicles.filter(v => 
        v.detailStatus === 'pending' || 
        v.detailStatus === 'in_progress' || 
        v.detailStatus === 'completed' ||
        v.reconStatus === 'detail_pending'
      );
      setVehicles(detailVehicles);
    } catch (error) {
      console.error('Failed to load detail vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, []);

  const handleStartDetail = async (vehicle) => {
    try {
      await api.updateVehicle(vehicle.id, { 
        detailStatus: 'in_progress'
      });
      loadVehicles();
    } catch (error) {
      console.error('Failed to start detail:', error);
      alert('Failed to start detail');
    }
  };

  const handleCompleteDetail = async (vehicle, hasExtraCharge = false) => {
    if (hasExtraCharge) {
      setExtraChargeModal({ show: true, vehicle });
      return;
    }

    setCompletingId(vehicle.id);
    try {
      await api.updateVehicle(vehicle.id, { 
        detailStatus: 'completed',
        detailCompletedDate: new Date().toISOString().split('T')[0],
        reconStatus: 'ready'
      });
      
      // Notify admin
      await api.createNotification({
        type: 'detail_complete',
        title: `Detail Complete: ${vehicle.stockNumber}`,
        message: `${vehicle.year} ${vehicle.make} ${vehicle.model} detail is complete`,
        vehicleId: vehicle.id,
        targetRole: 'admin'
      });
      
      loadVehicles();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to complete detail:', error);
      alert('Failed to complete detail');
    } finally {
      setCompletingId(null);
    }
  };

  const handleExtraChargeSubmit = async () => {
    const vehicle = extraChargeModal.vehicle;
    setCompletingId(vehicle.id);
    
    try {
      // Update vehicle
      await api.updateVehicle(vehicle.id, { 
        detailStatus: 'completed',
        detailCompletedDate: new Date().toISOString().split('T')[0],
        detailNotes: `Extra charge: $${extraCharge.amount} - ${extraCharge.reason}`,
        reconStatus: 'ready'
      });
      
      // Create expense for extra detail charge
      // Note: This would need an expense API endpoint
      
      // Notify admin
      await api.createNotification({
        type: 'detail_complete',
        title: `Detail Complete: ${vehicle.stockNumber}`,
        message: `${vehicle.year} ${vehicle.make} ${vehicle.model} detail complete with extra charge: $${extraCharge.amount}`,
        vehicleId: vehicle.id,
        targetRole: 'admin'
      });
      
      setExtraChargeModal({ show: false, vehicle: null });
      setExtraCharge({ amount: 0, reason: '' });
      loadVehicles();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to complete detail with extra charge:', error);
      alert('Failed to complete detail');
    } finally {
      setCompletingId(null);
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    if (filter === 'all') return true;
    if (filter === 'pending') return v.detailStatus === 'pending' || v.reconStatus === 'detail_pending';
    if (filter === 'in_progress') return v.detailStatus === 'in_progress';
    if (filter === 'completed') return v.detailStatus === 'completed';
    return true;
  });

  const counts = {
    all: vehicles.length,
    pending: vehicles.filter(v => v.detailStatus === 'pending' || v.reconStatus === 'detail_pending').length,
    in_progress: vehicles.filter(v => v.detailStatus === 'in_progress').length,
    completed: vehicles.filter(v => v.detailStatus === 'completed').length,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900"> Detail Dashboard</h1>
          <p className="text-sm text-neutral-500">Vehicles ready for detail</p>
        </div>
        <button
          onClick={loadVehicles}
          className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg"
        >
           Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'pending', label: 'Pending', count: counts.pending, color: 'yellow' },
          { id: 'in_progress', label: 'In Progress', count: counts.in_progress, color: 'blue' },
          { id: 'completed', label: 'Completed', count: counts.completed, color: 'green' },
          { id: 'all', label: 'All', count: counts.all, color: 'gray' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.id
                ? tab.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                : tab.color === 'blue' ? 'bg-blue-100 text-blue-800 border-2 border-blue-300'
                : tab.color === 'green' ? 'bg-green-100 text-green-800 border-2 border-green-300'
                : 'bg-neutral-200 text-neutral-800 border-2 border-neutral-400'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Vehicle List */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-neutral-400">Loading...</div>
        ) : filteredVehicles.length === 0 ? (
          <div className="p-8 text-center text-neutral-400">
            <div className="text-4xl mb-2"></div>
            <p>No vehicles in this category</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Vehicle</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">Notes</th>
                <th className="px-4 py-3 text-right font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filteredVehicles.map(vehicle => (
                <tr key={vehicle.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900">{vehicle.stockNumber}</div>
                    <div className="text-xs text-neutral-500">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </div>
                    {vehicle.color && (
                      <div className="text-xs text-neutral-400">{vehicle.color}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {vehicle.detailStatus === 'pending' || vehicle.reconStatus === 'detail_pending' ? (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                        Pending
                      </span>
                    ) : vehicle.detailStatus === 'in_progress' ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        In Progress
                      </span>
                    ) : vehicle.detailStatus === 'completed' ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        ✓ Completed
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-neutral-100 text-neutral-600 rounded-full text-xs">
                        {vehicle.detailStatus || 'Unknown'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {vehicle.detailNotes || '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(vehicle.detailStatus === 'pending' || vehicle.reconStatus === 'detail_pending') && (
                      <button
                        onClick={() => handleStartDetail(vehicle)}
                        className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600"
                      >
                        Start Detail
                      </button>
                    )}
                    {vehicle.detailStatus === 'in_progress' && (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleCompleteDetail(vehicle, false)}
                          disabled={completingId === vehicle.id}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 disabled:opacity-50"
                        >
                          {completingId === vehicle.id ? '...' : '✓ Complete'}
                        </button>
                        <button
                          onClick={() => handleCompleteDetail(vehicle, true)}
                          disabled={completingId === vehicle.id}
                          className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 disabled:opacity-50"
                        >
                          + Extra Charge
                        </button>
                      </div>
                    )}
                    {vehicle.detailStatus === 'completed' && vehicle.detailCompletedDate && (
                      <span className="text-xs text-neutral-400">
                        {new Date(vehicle.detailCompletedDate).toLocaleDateString()}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Extra Charge Modal */}
      {extraChargeModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Extra Detail Charge</h3>
            <p className="text-sm text-neutral-600 mb-4">
              Vehicle: {extraChargeModal.vehicle?.stockNumber} - {extraChargeModal.vehicle?.year} {extraChargeModal.vehicle?.make} {extraChargeModal.vehicle?.model}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Extra Charge Amount ($)
                </label>
                <input
                  type="number"
                  value={extraCharge.amount}
                  onChange={(e) => setExtraCharge(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Reason
                </label>
                <textarea
                  value={extraCharge.reason}
                  onChange={(e) => setExtraCharge(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                  rows={3}
                  placeholder="Why was there an extra charge?"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setExtraChargeModal({ show: false, vehicle: null });
                  setExtraCharge({ amount: 0, reason: '' });
                }}
                className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExtraChargeSubmit}
                disabled={!extraCharge.amount || !extraCharge.reason}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                Complete with Charge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailDashboard;
